import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    window.addEventListener('resize', this.#handleResize, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#submenuResizeObserver.disconnect();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #featuredUpdateTokens = new WeakMap();

  /**
   * Garde `--submenu-height` alignée sur la hauteur réelle du panneau ouvert. La mesure
   * faite au survol se périme si le contenu change de taille ensuite (barre de défilement,
   * polices, images en ratio fixe qui suivent la largeur), et `resize` ne le voit pas.
   */
  #submenuResizeObserver = new ResizeObserver((entries) => {
    const submenu = entries[entries.length - 1]?.target;
    if (!(submenu instanceof HTMLElement) || !this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  });

  #resizeToken = 0;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    // If the item was just closed, it may still be flagged as animating.
    // That state forces submenu content opacity to 0 in CSS, which looks like an "empty/white" mega menu.
    item?.removeAttribute('data-animating');

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#submenuResizeObserver.disconnect();

    if (submenu) {
      this.#scheduleFeaturedContentUpdate(submenu, item);
      this.#submenuResizeObserver.observe(submenu);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      const related = /** @type {any} */ (event).relatedTarget;
      const leavingMenu = !(related instanceof Node) || !this.contains(related);
      // Si on quitte réellement la zone du menu (vers la page), on ferme immédiatement.
      // Ça évite les cas où le debounce est retardé (throttling) ou annulé par des ré-entrées.
      if (leavingMenu) {
        this.#deactivate(item);
        return;
      }

      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#submenuResizeObserver.disconnect();
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #handleResize = debounce(() => {
    this.#resizeToken += 1;
    const item = this.#state.activeItem;
    if (!item) return;

    let submenu = findSubmenu(item);
    if (!submenu) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#scheduleFeaturedContentUpdate(submenu, item, true);
    }
  }, 100);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    // The desktop menu is display: none on mobile: its images would never be shown
    if (!this.checkVisibility()) return;

    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Schedule a single featured content update per open.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   * @param {boolean} force
   */
  #scheduleFeaturedContentUpdate = (submenu, item, force = false) => {
    const currentToken = this.#resizeToken;
    if (!force && this.#featuredUpdateTokens.get(submenu) === currentToken) return;

    requestAnimationFrame(() => {
      if (this.#state.activeItem !== item) return;
      this.#updateFeaturedContentVisibility(submenu);
      this.#featuredUpdateTokens.set(submenu, currentToken);
    });
  };

  /**
   * Show featured content only when submenu inner doesn't scroll, and re-measure the
   * submenu once its content has been laid out.
   * @param {HTMLElement} submenu
   */
  #updateFeaturedContentVisibility = (submenu) => {
    if (!(submenu instanceof HTMLElement)) return;

    // Un panneau composé de blocs `_menu-item` n'a pas de contenu « featured ». Seule la
    // re-mesure de hauteur le concerne, d'où la condition plutôt qu'un retour anticipé :
    // sans elle le header garderait la hauteur lue avant la mise en page du panneau.
    const featuredContent = submenu.querySelector('.mega-menu__featured-content');
    if (featuredContent instanceof HTMLElement) {
      const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
      const scrollContainer =
        submenuInner instanceof HTMLElement ? submenuInner : submenu;

      const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
      if (hasScroll) {
        featuredContent.style.display = 'none';
      } else {
        featuredContent.style.removeProperty('display');
      }
    }

    const submenuHeight = submenu.offsetHeight;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
